import React, { useState } from 'react';
import { Upload, Button, message, Image, Space, Typography } from 'antd';
import { UploadOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import type { RcFile } from 'antd/es/upload/interface';

const { Text } = Typography;

interface ImageUploadProps {
  value?: string[];
  onChange?: (urls: string[]) => void;
  maxCount?: number;
  folder?: string;
  disabled?: boolean;
}

interface UploadedImage {
  url: string;
  publicId: string;
  width: number;
  height: number;
  format: string;
  size: number;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  value = [],
  onChange,
  maxCount = 5,
  folder = 'general',
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [uploadedImages, setUploadedImages] = useState<UploadedImage[]>([]);

  const uploadToServer = async (file: RcFile): Promise<UploadedImage> => {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch('/api/upload/image', {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Upload failed');
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Upload failed');
    }

    return result.data;
  };

  const handleUpload = async (file: RcFile) => {
    if (value.length >= maxCount) {
      message.error(`Maximum ${maxCount} images allowed`);
      return false;
    }

    setUploading(true);
    try {
      const uploadedImage = await uploadToServer(file);
      
      const newImages = [...uploadedImages, uploadedImage];
      const newUrls = [...value, uploadedImage.url];
      
      setUploadedImages(newImages);
      onChange?.(newUrls);
      
      message.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      message.error(`Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
    } finally {
      setUploading(false);
    }

    return false; // Prevent default upload behavior
  };

  const handleRemove = async (index: number) => {
    try {
      const imageToRemove = uploadedImages[index];
      if (imageToRemove?.publicId) {
        // Delete from Cloudinary
        const token = localStorage.getItem('cleveroo_token') || localStorage.getItem('accessToken') || '';
        await fetch(`/api/upload/image/${encodeURIComponent(imageToRemove.publicId)}`, {
          method: 'DELETE',
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
      }

      const newImages = uploadedImages.filter((_, i) => i !== index);
      const newUrls = value.filter((_, i) => i !== index);
      
      setUploadedImages(newImages);
      onChange?.(newUrls);
      
      message.success('Image removed successfully!');
    } catch (error) {
      console.error('Remove error:', error);
      message.error('Failed to remove image');
    }
  };

  const beforeUpload = (file: RcFile) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('You can only upload image files!');
      return false;
    }

    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('Image must be smaller than 10MB!');
      return false;
    }

    return true;
  };

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }}>
        {/* Upload Button */}
        <Upload
          beforeUpload={beforeUpload}
          customRequest={({ file }) => handleUpload(file as RcFile)}
          showUploadList={false}
          disabled={disabled || uploading || value.length >= maxCount}
        >
          <Button 
            icon={<UploadOutlined />} 
            loading={uploading}
            disabled={disabled || value.length >= maxCount}
          >
            {uploading ? 'Uploading...' : 'Upload Image'}
          </Button>
        </Upload>

        {/* Upload Info */}
        <Text type="secondary" style={{ fontSize: '12px' }}>
          Maximum {maxCount} images, up to 10MB each. Supported formats: JPG, PNG, GIF, WebP
        </Text>

        {/* Image Preview Grid */}
        {value.length > 0 && (
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
            gap: '16px',
            marginTop: '16px'
          }}>
            {value.map((url, index) => (
              <div 
                key={index}
                style={{ 
                  position: 'relative',
                  border: '1px solid #d9d9d9',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  backgroundColor: '#fafafa'
                }}
              >
                <Image
                  src={url}
                  alt={`Uploaded ${index + 1}`}
                  style={{ 
                    width: '100%', 
                    height: '150px', 
                    objectFit: 'cover' 
                  }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAMIAAADDCAYAAADQvc6UAAABRWlDQ1BJQ0MgUHJvZmlsZQAAKJFjYGASSSwoyGFhYGDIzSspCnJ3UoiIjFJgf8LAwSDCIMogwMCcmFxc4BgQ4ANUwgCjUcG3awyMIPqyLsis7PPOq3QdDFcvjV3jOD1boQVTPQrgSkktTgbSf4A4LbmgqISBgTEFyFYuLykAsTuAbJEioKOA7DkgdjqEvQHEToKwj4DVhAQ5A9k3gGyB5IxEoBmML4BsnSQk8XQkNtReEOBxcfXxUQg1Mjc0dyHgXNJBSWpFCYh2zi+oLMpMzyhRcASGUqqCZ16yno6CkYGRAQMDKMwhqj/fAIcloxgHQqxAjIHBEugw5sUIsSQpBobtQPdLciLEVJYzMPBHMDBsayhILEqEO4DxG0txmrERhM29nYGBddr//5/DGRjYNRkY/l7////39v///y4Dmn+LgeHANwDrkl1AuO+pmgAAADhlWElmTU0AKgAAAAgAAYdpAAQAAAABAAAAGgAAAAAAAqACAAQAAAABAAAAwqADAAQAAAABAAAAwwAAAAD9b/HnAAAHlklEQVR4Ae3dP3Ik1RnG4W+FgYxN..."
                />
                
                {!disabled && (
                  <Button
                    type="text"
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemove(index)}
                    style={{
                      position: 'absolute',
                      top: '8px',
                      right: '8px',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      border: 'none'
                    }}
                  />
                )}

                {/* Image Info */}
                {uploadedImages[index] && (
                  <div style={{
                    position: 'absolute',
                    bottom: '0',
                    left: '0',
                    right: '0',
                    background: 'rgba(0, 0, 0, 0.7)',
                    color: 'white',
                    padding: '4px 8px',
                    fontSize: '11px'
                  }}>
                    {uploadedImages[index].width}×{uploadedImages[index].height} • {uploadedImages[index].format.toUpperCase()} • {Math.round(uploadedImages[index].size / 1024)}KB
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Empty State */}
        {value.length === 0 && (
          <div style={{
            border: '2px dashed #d9d9d9',
            borderRadius: '8px',
            padding: '40px 20px',
            textAlign: 'center',
            color: '#999'
          }}>
            <PictureOutlined style={{ fontSize: '48px', marginBottom: '16px' }} />
            <div>No images uploaded yet</div>
            <div style={{ fontSize: '12px', marginTop: '8px' }}>
              Click "Upload Image" to add images
            </div>
          </div>
        )}
      </Space>
    </div>
  );
};

export default ImageUpload;